import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  forwardRef,
} from 'react'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import { SECONDS_IN_MINUTE } from 'utils/constants'
import { Button } from 'components/UIElements'
import { checkmarkIcon, microphoneIcon } from '../../../../assets/assets'
import { logSentryEvent, SENTRY_MESSAGES } from 'utils/sentry'

const INITIAL_COUNTDOWN_SECONDS = 3
const RECORDING_STATE = {
  notStarted: 'notStarted',
  inProgress: 'inProgress',
  completed: 'completed',
}

const MESSAGES = {
  initial: 'Voice recording will start in...',
  recording: 'Recording...',
  complete: 'Recording complete',
  accessDenied: 'Please allow access to your microphone',
  requestingPermission: 'Requesting microphone access...',
}

const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

const getSupportedAudioMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return ''
  }
  return (
    AUDIO_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ||
    ''
  )
}

const getAudioFileExtension = (mimeType = '') => {
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

/** Persist recording filename into Redux without advancing (sibling / suppressAutoAdvance). */
const persistAudioSubmission = (props, filename) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = filename
      ? [
          {
            label,
            value: filename,
            question: id,
          },
        ]
      : []
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: filename ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('AudioType autosave failed', { error: e })
  }
}

const completeAudioTypeQuestion = (props, text) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = text
    ? [
        {
          label,
          value: text,
          question: id,
        },
      ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: text ? 'ANSWERED' : 'SKIPPED',
    answer,
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

const AudioType = forwardRef((props, ref) => {
  const {
    className,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    strings,
    uploadV2File,
    studyId,
    webSurvey,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    isSibling = false,
  } = props
  const { attributes, label, imageSrc } = question
  const {
    required,
    min: timerSeconds = 0,
    show_timer: showTimer,
    max: maxSeconds = 0,
  } = attributes
  const isRange = maxSeconds > timerSeconds
  const rangeDiff = isRange ? maxSeconds - timerSeconds : 0

  const [audio, setAudio] = useState('')
  const [audioFilename, setAudioFilename] = useState('')
  const [recordingState, setRecordingState] = useState(
    RECORDING_STATE.notStarted,
  )
  const [initialTimeRemaining, setInitialTimeRemaining] = useState(
    INITIAL_COUNTDOWN_SECONDS,
  )
  const [recordingTimeRemaining, setRecordingTimeRemaining] = useState(
    isRange ? maxSeconds : timerSeconds,
  )
  const [textMessage, setTextMessage] = useState(MESSAGES.requestingPermission)
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState(false)
  const [isRequestingPermission, setIsRequestingPermission] = useState(true)

  const recorderRef = useRef(null) // Ref to store the recorder instance
  const recordingTimerIntervalRef = useRef(null) // Ref to store the interval ID
  const streamRef = useRef(null) // Ref to store the media stream
  const propsRef = useRef(props)
  const startAudioRecordRef = useRef(null)
  propsRef.current = props

  const _className = className ? `${className}` : ''

  const applyMicrophoneGranted = useCallback((action = 'accepted') => {
    setHasMicrophonePermission(true)
    setTextMessage(MESSAGES.initial)
    setIsRequestingPermission(false)
    setInitialTimeRemaining(INITIAL_COUNTDOWN_SECONDS)
    logSentryEvent({
      message:
        action === 'granted'
          ? SENTRY_MESSAGES.permissionGranted
          : SENTRY_MESSAGES.permissionAccepted,
      tags: {
        flow: 'permissions',
        action,
        permission_type: 'microphone',
      },
    })
  }, [])

  const applyMicrophoneDenied = useCallback((error) => {
    const denied =
      error?.name === 'NotAllowedError' ||
      error?.name === 'PermissionDeniedError'
    logSentryEvent({
      message: denied
        ? SENTRY_MESSAGES.permissionDenied
        : SENTRY_MESSAGES.permissionError,
      level: denied ? 'info' : 'error',
      tags: {
        flow: 'permissions',
        action: denied ? 'denied' : 'error',
        permission_type: 'microphone',
      },
      error: denied ? undefined : error,
    })
    console.warn('Microphone permission denied:', error)
    setTextMessage(MESSAGES.accessDenied)
    setHasMicrophonePermission(false)
    setIsRequestingPermission(false)
  }, [])

  const requestMicrophonePermission = useCallback(async () => {
    setIsRequestingPermission(true)
    setTextMessage(MESSAGES.requestingPermission)
    try {
      // Always verify with getUserMedia. Permissions API / enumerateDevices can
      // report "granted" while Permissions-Policy or a missing device still blocks recording.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      })

      streamRef.current = stream
      stream.getTracks().forEach((track) => track.stop())
      applyMicrophoneGranted('accepted')
    } catch (error) {
      applyMicrophoneDenied(error)
    } finally {
      setIsRequestingPermission(false)
    }
  }, [applyMicrophoneDenied, applyMicrophoneGranted])

  // Request microphone permission on component mount and retry when the user
  // later grants access from the browser site-settings UI.
  useEffect(() => {
    let permissionStatus = null
    let cancelled = false

    if (storedAnswer?.[0]?.value) {
      setIsRequestingPermission(false)
    } else {
      requestMicrophonePermission()
    }

    const subscribeToPermissionChanges = async () => {
      if (!navigator.permissions?.query) return
      try {
        permissionStatus = await navigator.permissions.query({
          name: 'microphone',
        })
        permissionStatus.onchange = () => {
          if (cancelled || storedAnswer?.[0]?.value) return
          if (permissionStatus.state === 'granted') {
            requestMicrophonePermission()
          } else if (permissionStatus.state === 'denied') {
            applyMicrophoneDenied({ name: 'NotAllowedError' })
          }
        }
      } catch (_) {
        // Permissions API for microphone not supported (e.g. Safari)
      }
    }
    subscribeToPermissionChanges()

    return () => {
      cancelled = true
      if (permissionStatus) permissionStatus.onchange = null
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [storedAnswer, requestMicrophonePermission, applyMicrophoneDenied])

  // Restore draft answer once on mount (same pattern as TextEntry / VASHorizontal).
  // Do not depend on `props` or `storedAnswer` — parent re-renders recreate both and
  // calling completeQuestion in a loop causes "Maximum update depth exceeded".
  useEffect(() => {
    const storedFilename = storedAnswer?.[0]?.value
    if (!storedFilename) return

    setAudioFilename(storedFilename)
    setRecordingState(RECORDING_STATE.completed)
    setTextMessage(MESSAGES.complete)
    const currentProps = propsRef.current
    if (!currentProps.suppressAutoAdvance) {
      completeAudioTypeQuestion(currentProps, storedFilename)
    } else {
      persistAudioSubmission(currentProps, storedFilename)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopAudioRecord = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop()
      setTextMessage(MESSAGES.complete)
      setRecordingState(RECORDING_STATE.completed)

      // Clear the interval if it exists
      if (recordingTimerIntervalRef.current) {
        clearInterval(recordingTimerIntervalRef.current)
        recordingTimerIntervalRef.current = null
      }

      // Stop the stream tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }
    }
  }, [])

  const startAudioRecord = useCallback(async () => {
    // Don't start if we don't have permission
    if (!hasMicrophonePermission) {
      setTextMessage(MESSAGES.accessDenied)
      return
    }

    const chunks = []

    try {
      // Get a new stream for recording
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })

      // Store the stream
      streamRef.current = stream

      const mimeType = getSupportedAudioMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      recorderRef.current = recorder
      const blobType = mimeType || recorder.mimeType || 'audio/webm'
      const fileExtension = getAudioFileExtension(blobType)

      recorder.ondataavailable = (e) => {
        chunks.push(e.data)
        if (recorder.state === 'inactive') {
          const blob = new Blob(chunks, { type: blobType })
          const testAudioRecord = URL.createObjectURL(blob)
          setAudio(testAudioRecord)
          if (uploadV2File) {
            const filename = `${studyId}_${
              webSurvey?.id
            }_${id}_${Date.now()}.${fileExtension}`
            const file = new File([blob], filename, {
              type: blobType,
              lastModified: new Date().getTime(),
            })
            const fData = new FormData()
            fData.set('upload_file', file)
            fData.set('file_name', filename)
            fData.set('module_name', 'audio')
            uploadV2File(fData).then(() => {
              setAudioFilename(filename)
              persistAudioSubmission(propsRef.current, filename)
            })
          }
        }
      }

      recorder.start(1000)
      setTextMessage(MESSAGES.recording)
      setRecordingState(RECORDING_STATE.inProgress)

      // Start the countdown timer
      recordingTimerIntervalRef.current = setInterval(() => {
        setRecordingTimeRemaining((prevTime) => {
          if (prevTime === 0) {
            stopAudioRecord() // Stop recording when time runs out
            return 0
          }
          return prevTime - 1
        })
      }, 1000)
    } catch (e) {
      setTextMessage(MESSAGES.accessDenied)
      setHasMicrophonePermission(false)
      console.warn('Error getting stream', e)
    }
  }, [
    uploadV2File,
    studyId,
    webSurvey?.id,
    id,
    stopAudioRecord,
    hasMicrophonePermission,
  ])

  startAudioRecordRef.current = startAudioRecord

  useEffect(() => {
    // Only start countdown if we have permission and no stored answer
    if (
      storedAnswer?.[0]?.value ||
      !hasMicrophonePermission ||
      isRequestingPermission
    ) {
      return
    }

    const timerInterval = setInterval(() => {
      setInitialTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerInterval)
          startAudioRecordRef.current?.()
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => clearInterval(timerInterval)
  }, [storedAnswer, hasMicrophonePermission, isRequestingPermission])

  const formattedRecordingTimeRemaining = useMemo(() => {
    const minutes = Math.floor(recordingTimeRemaining / SECONDS_IN_MINUTE)
    const seconds = recordingTimeRemaining % SECONDS_IN_MINUTE
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}`
  }, [recordingTimeRemaining])

  const isRecording = recordingState === RECORDING_STATE.inProgress
  const disabled =
    (required && !audioFilename) || isRecording || isRequestingPermission
  const canRetryPermission =
    !hasMicrophonePermission &&
    !isRequestingPermission &&
    recordingState === RECORDING_STATE.notStarted &&
    !storedAnswer?.[0]?.value

  const handleRetryPermission = () => {
    if (!canRetryPermission) return
    requestMicrophonePermission()
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div ref={ref} className={`survey-question audio-entry${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label),
              }}
            />
            <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
          </p>
          <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
          <div
            className={`audio-recording-box${
              canRetryPermission ? ' audio-recording-box--retry' : ''
            }`}
            onClick={canRetryPermission ? handleRetryPermission : undefined}
            onKeyDown={
              canRetryPermission
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleRetryPermission()
                    }
                  }
                : undefined
            }
            role={canRetryPermission ? 'button' : undefined}
            tabIndex={canRetryPermission ? 0 : undefined}
            title={canRetryPermission ? 'Click to try again' : undefined}
          >
            <div className='audio-recording-inner'>
              <div className='audio-title-box'>
                <div className='audio-title-icon'>
                  {recordingState === RECORDING_STATE.inProgress
                    ? microphoneIcon()
                    : null}
                  {recordingState === RECORDING_STATE.completed
                    ? checkmarkIcon()
                    : null}
                </div>
                <div className='audio-title-text'>{textMessage}</div>
              </div>

              {isRequestingPermission ? (
                <div className='audio-countdown'>Requesting access...</div>
              ) : (
                <>
                  {!!initialTimeRemaining &&
                  hasMicrophonePermission &&
                  !storedAnswer?.[0]?.value ? (
                    <div className='audio-countdown'>
                      {initialTimeRemaining}
                    </div>
                  ) : null}

                  {!initialTimeRemaining &&
                  recordingTimeRemaining &&
                  showTimer &&
                  hasMicrophonePermission &&
                  !storedAnswer?.[0]?.value ? (
                    <div className='audio-countdown'>
                      {formattedRecordingTimeRemaining}
                    </div>
                  ) : null}

                  {recordingState === RECORDING_STATE.inProgress &&
                    isRange &&
                    rangeDiff >= recordingTimeRemaining && (
                      <Button
                        className='stop-button'
                        onClick={stopAudioRecord}
                        link
                        icon='fa fa-stop-circle-o fa-2x'
                        aria-label='Stop recording'
                      />
                    )}
                </>
              )}
            </div>
          </div>

          {!isSibling && (
            <div className='character-count-row'>
              <QuestionFooter
                backButtonDisabled={disabled}
                completed={!!audioFilename}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() =>
                  completeAudioTypeQuestion(props, audioFilename)
                }
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            </div>
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default AudioType

AudioType.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  imageSrc: PropTypes.string,
}
